import type { NodeRendererProps } from "@hogartrabe/react-arborist-vrh";
import cx from "classnames";
import { Icon } from "components/Icon/Icon";
import type { useProjectTreeContentTranslations } from "hooks/projectTreeHooks";
import type {
  NavigateToCustomProductProps,
  NavigateToProductProps,
} from "interfaces/product/product.types";
import type { Node as NodeType } from "interfaces/schemas";
import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  useCopiedItem,
  useProject,
  useProjectActions,
  useProjectUIActions,
  useSetAddedProducts,
  useSetCopiedItem,
  useSetHasAddedNode,
} from "stores/project";
import { getIsAncestorDragging, getScrollToIndex } from "utils";
import * as identify from "utils/treeUtils";
import type { defaultOptions } from "variables/nodeDefaultOptions";
import { Configuration } from "./Configuration/Configuration";
import { Folder } from "./Folder/Folder";
import { NodeLayout } from "./NodeLayout";
import { NodeVisibility } from "./NodeVisibility";
import { useIndentComponent } from "hooks/projectTreeHooks";

export type NodeProps = NodeRendererProps<NodeType> & {
  options: typeof defaultOptions;
  translations: ReturnType<typeof useProjectTreeContentTranslations>;
  setScrollToIndex: (index: number) => void;
  navigateToProduct: (props: NavigateToProductProps) => void;
  navigateToCustomProduct: (data: NavigateToCustomProductProps) => void;
  lang: string;
};

export const Node = ({
  node,
  tree,
  style,
  dragHandle,
  options,
  translations,
  setScrollToIndex,
  navigateToProduct,
  lang,
  navigateToCustomProduct,
}: NodeProps) => {
  const actions = useProjectActions();
  const copiedItem = useCopiedItem();
  const setCopiedItem = useSetCopiedItem();
  const project = useProject();
  const { toggleNode, setRedrawIndex } = useProjectUIActions();
  const setHasAddedNode = useSetHasAddedNode();
  const setAddedProducts = useSetAddedProducts();
  // Props
  const { data, rowIndex, children, isOpen, isDragging, willReceiveDrop } =
    node;

  // Hooks
  const [isEditMode, setIsEditMode] = useState<boolean | string>(false);
  const indent = useIndentComponent(
    { ...node, rowIndex: node.rowIndex ?? 0 },
    options,
  );

  const isAncestorDragging = getIsAncestorDragging(node);

  const isPasteAllowed =
    copiedItem &&
    !(identify.isAnyProduct(copiedItem.data) && identify.isRoot(data)) &&
    (identify.isFolder(data) || identify.isRoot(data));

  const projectTitle = project?.title ?? "";

  const handlePasteNode = useCallback(() => {
    if (!isPasteAllowed) {
      return;
    }

    if (rowIndex !== null) {
      setRedrawIndex(rowIndex);
    }

    const handleProductPasted = (pastedNode: NodeType) => {
      if (!identify.isAnyProduct(pastedNode) || !options.productAddingModal) {
        return;
      }

      setAddedProducts(pastedNode.id);
      setHasAddedNode();
      toast.success(
        translations.product.productAddedSuccess({
          productName: pastedNode.productName ?? "",
          projectName: projectTitle,
        }),
      );
    };

    try {
      actions.tree.pasteNode(node, handleProductPasted);
    } catch (err) {
      toast.error(err instanceof Error && err.message);
      return;
    }

    if (options.resetOnPaste) {
      setCopiedItem(null);
    }

    if (!isOpen) {
      node.toggle();
      toggleNode(data.id);
    }

    setScrollToIndex(
      getScrollToIndex(node, identify.isAnyProduct(copiedItem.data)),
    );
  }, [
    isPasteAllowed,
    rowIndex,
    options.resetOnPaste,
    options.productAddingModal,
    isOpen,
    setScrollToIndex,
    node,
    copiedItem,
    setRedrawIndex,
    setAddedProducts,
    setHasAddedNode,
    translations.product,
    projectTitle,
    actions.tree,
    setCopiedItem,
    toggleNode,
    data.id,
  ]);

  const dragNode =
    tree.dragNodes && tree.dragNodes.length > 0 ? tree.dragNodes[0] : null;

  // Handlers
  const handleToggleEditMode = useCallback(
    (type: string | boolean = false) => {
      if (
        identify.isFolder(data) ||
        identify.isProduct(data) ||
        identify.isCustomProduct(data) ||
        identify.isProductAccessory(data)
      ) {
        if (!isEditMode) {
          node.edit();

          setIsEditMode(type);
          return;
        }
        node.reset();
        setIsEditMode(false);
      }
    },
    [data, isEditMode, node],
  );

  const handleToggleNodeActiveStatus = useCallback(
    () => actions.tree.toggleNodeActiveStatus(node),
    [actions, node],
  );

  const onDoubleClick = useCallback(() => {
    if (isEditMode || copiedItem) {
      return;
    }
    if (identify.isProduct(data) || identify.isProductAccessory(data)) {
      navigateToProduct(data);
    }
    if (
      identify.isTroxNetcomProduct(data) ||
      identify.isCustomProduct(data) ||
      identify.isTroxNetcomNote(data) ||
      identify.isTroxNetcomGenericProduct(data)
    ) {
      navigateToCustomProduct(data);
    }
  }, [
    copiedItem,
    data,
    isEditMode,
    navigateToCustomProduct,
    navigateToProduct,
  ]);

  const productIdURL =
    new URL(window.location.href).searchParams.get("productId") || null;

  const isActiveProductNode = node.data?.id === productIdURL;

  return (
    <div
      ref={!isEditMode ? dragHandle : null}
      style={style}
      className={cx("w-100 h-100", {
        "pe-9": options.isFullpage,
        "pe-2": !options.isFullpage,
      })}
    >
      <div className="w-100 h-100 d-flex flex-row">
        {indent}
        <NodeLayout
          node={node}
          options={options}
          hasChildren={!!(children && children.length > 0)}
          isOpen={isOpen}
          isAncestorDragging={isAncestorDragging}
          isDragging={isDragging}
          willReceiveDrop={willReceiveDrop}
          onDoubleClick={onDoubleClick}
          dragNode={dragNode}
          onClick={handlePasteNode}
          isPasteAllowed={isPasteAllowed}
          isActiveProductNode={isActiveProductNode}
        >
          {identify.isAnyProduct(node.data) ? (
            <Configuration
              node={node}
              options={options}
              translations={translations}
              navigateToProduct={navigateToProduct}
              handleToggleEditMode={handleToggleEditMode}
              setIsEditMode={setIsEditMode}
              isEditMode={isEditMode}
              lang={lang}
              navigateToCustomProduct={navigateToCustomProduct}
            />
          ) : (
            <Folder
              node={node}
              options={options}
              translations={translations}
              setScrollToIndex={setScrollToIndex}
              willReceiveDrop={willReceiveDrop}
              handleToggleEditMode={handleToggleEditMode}
              isEditMode={isEditMode}
              handleToggleNodeActiveStatus={handleToggleNodeActiveStatus}
              setIsEditMode={setIsEditMode}
            />
          )}
        </NodeLayout>
        <div className="ps-2 h-100 d-flex align-items-center">
          {identify.isWizardCommissioningCost(data) ? (
            <Icon
              iconName="list-status-default"
              className={cx("icon-16", {
                "text-blue-300": data.active,
                "text-gray-100": !data.active,
              })}
            />
          ) : (
            <NodeVisibility
              node={node}
              onClick={handleToggleNodeActiveStatus}
            />
          )}
        </div>
      </div>
    </div>
  );
};
